import { getAssetLevelNetAvailabilities } from '@see-power-pft-common-js/net-availability/asset-level-net-availability';
import { INetAvailabilityError } from '@see-power-pft-common-js/net-availability/net-availability';
import { getSiteLevelNetAvailabilities } from '@see-power-pft-common-js/net-availability/site-level-net-availability';
import { NetAvailability } from '@see-power-pft-common-js/net-availability/types';
import { Result } from '@see-power-pft-common-js/types/result';
import { getResult } from '@see-power-pft-common-js/utils/result/utils';
import {
  getSettlementPeriodTimeWindows,
  IntervalDoesNotAlignToSpError,
} from '@see-power-pft-common-js/utils/sp';
import { DateInterval } from '@see-power-pft-common-js/utils/time-interval';
import { addSeconds } from 'date-fns';

type ChartEntry = {
  date: Date;
  maxImportPower: number;
  maxExportPower: number;
  energyCapacity: number;
};

export const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
export const ONE_WEEK_IN_MS = 7 * ONE_DAY_IN_MS;
export const ONE_MONTH_IN_MS = 30 * ONE_DAY_IN_MS;

export const determineAdditionalTimestamps = (options: {
  timeHorizon: DateInterval;
}): Result<IntervalDoesNotAlignToSpError, number[]> => {
  const startTime = +options.timeHorizon.start;
  const endTime = +options.timeHorizon.end;
  const timeHorizonDuration = endTime - startTime;

  if (timeHorizonDuration > ONE_MONTH_IN_MS) {
    const timestamps = [startTime];
    for (
      let current = +options.timeHorizon.start + ONE_WEEK_IN_MS;
      current <= +options.timeHorizon.end - ONE_WEEK_IN_MS;
      current += ONE_WEEK_IN_MS
    ) {
      timestamps.push(current);
    }

    timestamps.push(endTime);

    return getResult(timestamps);
  }

  if (timeHorizonDuration > ONE_WEEK_IN_MS) {
    const timestamps = [startTime];
    for (
      let current = +options.timeHorizon.start + ONE_DAY_IN_MS;
      current <= +options.timeHorizon.end - ONE_DAY_IN_MS;
      current += ONE_DAY_IN_MS
    ) {
      timestamps.push(current);
    }

    timestamps.push(endTime);

    return getResult(timestamps);
  }

  return getSettlementPeriodTimeWindows(options.timeHorizon).map(
    (settlementPeriodTimeWindows) => {
      const settlementPeriodTimestamps: number[] = Array.from(
        new Set(settlementPeriodTimeWindows.flat()),
      );

      return settlementPeriodTimestamps;
    },
  );
};

const getAvailabilityChartEntries = <
  TOptions extends { timeHorizon: DateInterval },
>(
  options: TOptions,
  getNetAvailabilities: (
    options: TOptions & { additionalTimestamps: number[] },
  ) => Result<INetAvailabilityError[], NetAvailability[]>,
): Result<
  INetAvailabilityError[] | IntervalDoesNotAlignToSpError,
  ChartEntry[]
> => {
  return determineAdditionalTimestamps(options)
    .flatMap((additionalTimestamps) =>
      getNetAvailabilities({
        ...options,
        additionalTimestamps,
      }),
    )
    .map((netAvailabilities) =>
      netAvailabilities.map(({ start, end, netPowerAndEnergyRatings }) => ({
        start,
        end,
        maxImportPower:
          netPowerAndEnergyRatings.site.maxImportPower === 0
            ? 0
            : -netPowerAndEnergyRatings.site.maxImportPower,
        maxExportPower: netPowerAndEnergyRatings.site.maxExportPower,
        energyCapacity: netPowerAndEnergyRatings.site.energyCapacity,
      })),
    )
    .map((entriesWithCorrectedMaxImportPower) =>
      entriesWithCorrectedMaxImportPower.reduce((acc, it, index) => {
        const newEntry: ChartEntry = {
          date: it.start,
          maxImportPower: it.maxImportPower,
          maxExportPower: it.maxExportPower,
          energyCapacity: it.energyCapacity,
        };

        const next = entriesWithCorrectedMaxImportPower[index + 1];

        const isLast = index === entriesWithCorrectedMaxImportPower.length - 1;

        if (isLast) {
          const newEntryAtEnd: ChartEntry = {
            date: it.end,
            maxImportPower: it.maxImportPower,
            maxExportPower: it.maxExportPower,
            energyCapacity: it.energyCapacity,
          };

          acc.push(newEntry);
          acc.push(newEntryAtEnd);
          return acc;
        }

        const isNextEntryValueDifferent =
          it.maxImportPower !== next?.maxImportPower ||
          it.maxExportPower !== next.maxExportPower ||
          it.energyCapacity !== next.energyCapacity;

        if (isNextEntryValueDifferent) {
          const newEntryAtEnd: ChartEntry = {
            date: addSeconds(it.end, -1),
            maxImportPower: it.maxImportPower,
            maxExportPower: it.maxExportPower,
            energyCapacity: it.energyCapacity,
          };

          acc.push(newEntry);
          acc.push(newEntryAtEnd);
          return acc;
        }

        acc.push(newEntry);
        return acc;
      }, [] as ChartEntry[]),
    );
};

export const getSiteLevelAvailabilityChartEntries = (
  options: Parameters<typeof getSiteLevelNetAvailabilities>[0],
): Result<
  INetAvailabilityError[] | IntervalDoesNotAlignToSpError,
  ChartEntry[]
> => getAvailabilityChartEntries(options, getSiteLevelNetAvailabilities);

export const getAssetLevelAvailabilityChartEntries = (
  options: Parameters<typeof getAssetLevelNetAvailabilities>[0],
): Result<
  INetAvailabilityError[] | IntervalDoesNotAlignToSpError,
  ChartEntry[]
> => getAvailabilityChartEntries(options, getAssetLevelNetAvailabilities);
